// [UNTAPPED-v2] Sidebar - Contains navigation link to Untapped Opportunities
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    Home,
    Users,
    Search,
    Eye,
    TrendingUp,
    BookOpen,
    BarChart3,
    Phone,
    MessageSquare,
    HelpCircle,
    FileText,
    Settings,
    User,
    CreditCard,
    Target,
    Mail,
    Box,
    ChevronRight,
    ChevronDown,
    Edit,
    UserCog,
    Radar,
    FileCheck,
    LayoutDashboard,
    Terminal,
    Trash2,
    Share2,
    Activity,
    DollarSign,
    Globe,
    ShieldCheck,
    ListOrdered,
    Link2,
    Shuffle,
    Bot
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { primeAccount, pageBlockReason, isTeamAdmin, isSubUser } from '../services/account';

const Sidebar = ({
sidebarCollapsed = false,
setSidebarCollapsed = () => {},
openMenus = {},
toggleMenu = () => {}
}) => {
const navigate = useNavigate();
const location = useLocation();
const [businessEmail, setBusinessEmail] = useState(null);
const [loading, setLoading] = useState(true);
// Impersonation state — when an admin is signed in "as" another user, show a
// banner with a "Return to admin" button.
const [isImpersonating, setIsImpersonating] = useState(false);
const [impersonatorEmail, setImpersonatorEmail] = useState(null);
const [stoppingImpersonation, setStoppingImpersonation] = useState(false);
// Team account summary — which features this person may use. null until
// loaded (and for older servers), which shows the full menu as before.
const [account, setAccount] = useState(null);
const { isDark } = useTheme();

// Fetch business email on component mount
useEffect(() => {
    const fetchBusinessEmail = async() => {
        try {
            const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3000';
            const response = await fetch(`${apiUrl}/api/sessionValue`, {
                method: 'GET',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            if (response.ok) {
                const result = await response.json();
                if (result.success) {
                    setBusinessEmail(result.data.businessEmail);
                    setIsImpersonating(!!result.data.isImpersonating);
                    setImpersonatorEmail(result.data.impersonatorEmail || null);
                    setAccount(result.data.account || null);
                    primeAccount(result.data.account);
                }
            }
        } catch (error) {
        } finally {
            setLoading(false);
        }
    };

    fetchBusinessEmail();
}, []);

// Return from an impersonated session back to the admin account, then reload
// into the admin User Panel.
const handleReturnToAdmin = async () => {
    if (stoppingImpersonation) return;
    setStoppingImpersonation(true);
    try {
        const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3000';
        const response = await fetch(`${apiUrl}/api/user-panel/stop-impersonate`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
        });
        const result = await response.json();
        if (response.ok && result.success) {
            window.location.href = '/user-panel';
        } else {
            alert('Failed to return to admin: ' + (result.message || 'Unknown error'));
            setStoppingImpersonation(false);
        }
    } catch (error) {
        alert('Network error while returning to admin');
        setStoppingImpersonation(false);
    }
};

// Check if user should see admin options (Update Brand and User Panel)
const showAdminOptions = businessEmail?.trim().toLowerCase() === 'mars.mediacle@gmail.com';

const baseMenuItems = [{
        key: 'dashboard',
        label: 'Dashboard',
        icon: Home,
        link: '/dashboard',
        route: '/dashboard'
    },
    {
        key: 'affiliate-recruitment',
        label: 'Affiliate Recruitment',
        icon: Users,
        link: '/affiliate-recruitment',
        route: '/affiliate-recruitment'
    },
    {
        key: 'competitorResearch',
        label: 'Competitor Research',
        icon: Search,
        hasSubmenu: true,
        isOpen: openMenus.competitorResearch || false,
        submenu: [
            { key: 'competitor-spy', label: 'Competitor Spy', icon: Eye, link: '/competitor-spy', route: '/competitor-spy' },
            { key: 'affiliate-gap', label: 'Affiliate Gap', icon: TrendingUp, link: '/affiliate-gap', route: '/affiliate-gap' }
        ]
    },
    // Every account has the agent team (the user, 21 Oct 2026). It does nothing
    // until that account finishes a team's setup and presses "Start team".
    // It sits after the first three items, which the admin splice below counts on.
    {
        key: 'agent-team',
        label: 'Agent Team',
        icon: Bot,
        link: '/agent-team',
        route: '/agent-team'
    },
    {
        key: 'affiliate-directory',
        label: 'Affiliate Directory',
        icon: BookOpen,
        link: '/affiliate-directory',
        route: '/affiliate-directory'
    },
    {
        key: 'crm-pipeline',
        label: 'Affiliate Pipeline',
        icon: LayoutDashboard,
        link: '/crm-pipeline',
        route: '/crm-pipeline'
    },
    {
        key: 'position-tracker',
        label: 'Position Tracker',
        icon: Target,
        link: '/position-tracker',
        route: '/position-tracker'
    },
    // {
    //     key: 'terms-checker',
    //     label: 'Terms Checker',
    //     icon: FileCheck,
    //     link: '/terms-checker',
    //     route: '/terms-checker'
    // },
    {
        key: 'keyword-explorer',
        label: 'Keywords Explorer',
        icon: BarChart3,
        link: '/keyword-explorer',
        route: '/keyword-explorer'
    },
    {
        key: 'serp-checker',
        label: 'SERP Checker',
        icon: TrendingUp,
        link: '/SerpChecker',
        route: '/SerpChecker'
    },

    // {
    //     key: 'sand-box',
    //     label: 'Sand Box',
    //     icon: Box,
    //     link: '/sand-box',
    //     route: '/sand-box'
    // },
    {
        key: 'contact-finder',
        label: 'Contacts Finder',
        icon: Phone,
        link: '/contact-finder',
        route: '/contact-finder'
    },
    {
        key: 'social-media-finder',
        label: 'Social Media Finder',
        icon: Share2,
        link: '/social-media-finder',
        route: '/social-media-finder'
    },
    {
        key: 'messages',
        label: 'Messages',
        icon: MessageSquare,
        hasSubmenu: true,
        isOpen: openMenus.messages || false,
        submenu: [
            { key: 'emails-sent', label: 'Inbox', icon: Mail, link: '/sent-box', route: '/sent-box' },
            // Affiliate Chat — temporarily hidden for MARS users; uncomment to re-enable.
            // { key: 'affiliate-chat', label: 'Affiliate Chat', icon: MessageSquare, link: '/messages', route: '/messages' },
            // AI Approval is gated to mars.mediacle@gmail.com only — same flag
            // used elsewhere for admin-only entries.
            ...(showAdminOptions ? [{ key: 'ai-approval', label: 'AI Approval', icon: FileCheck, link: '/ai-approval', route: '/ai-approval' }] : []),
            { key: 'email-settings', label: 'Email Settings', icon: Settings, link: '/email-settings', route: '/email-settings' }
        ]
    },
    {
        key: 'support',
        label: 'Support',
        icon: HelpCircle,
        hasSubmenu: true,
        isOpen: openMenus.support || false,
        submenu: [
            { key: 'help', label: 'Help', icon: HelpCircle, link: 'mailto:mars@mediacle.com' },
            { key: 'documentation', label: 'Documentation', icon: FileText, link: '/documentation', route: '/documentation' }
        ]
    },
    {
        key: 'settings',
        label: 'Settings',
        icon: Settings,
        hasSubmenu: true,
        isOpen: openMenus.settings || false,
        submenu: [
            { key: 'profile', label: 'Profile', icon: User, link: '/profile', route: '/profile' },
            // The account owner and co-admins manage who is on the team.
            ...(isTeamAdmin(account) ? [{ key: 'team', label: 'Team & Permissions', icon: UserCog, link: '/settings/team', route: '/settings/team' }] : []),
            // Billing belongs to the owner; team members never see it.
            ...(isSubUser(account) ? [] : [{ key: 'plans', label: 'Plans & Billing', icon: CreditCard, link: '/pricing-plan', route: '/pricing-plan' }]),
            // Show/hide admin-only tooling. Gated to mars.mediacle@gmail.com by
            // the same flag as the other admin-only entries.
            ...(showAdminOptions ? [{ key: 'admin-settings', label: 'Admin Settings', icon: ShieldCheck, link: '/admin-settings', route: '/admin-settings' }] : [])
        ]
    }
];

// Add admin-only options conditionally.
// Admin entries are spliced in after the first three base items; the tail must
// therefore resume at index 3, not 2 — slicing from 2 rendered Competitor
// Research twice and gave React two children with the same key.
const allMenuItems = showAdminOptions ? [
        ...baseMenuItems.slice(0, 3), // Dashboard, Affiliate Recruitment, Competitor Research
        {
            key: 'update-brand',
            label: 'Update Brand',
            icon: Edit,
            link: '/update-brand',
            route: '/update-brand'
        },
        {
            key: 'assign-brands',
            label: 'Assign Brands',
            icon: Share2,
            link: '/assign-brands',
            route: '/assign-brands'
        },
        {
            key: 'user-panel',
            label: 'User Panelss',
            icon: UserCog,
            link: '/user-panel',
            route: '/user-panel'
        },
        {
            key: 'affiliate-finder',
            label: 'Affiliate Finder',
            icon: Radar,
            link: '/affiliate-finder',
            route: '/affiliate-finder'
        },
        {
            key: 'live-affiliate-checker',
            label: 'Live Affiliate Checker',
            icon: Activity,
            link: '/live-affiliate-checker',
            route: '/live-affiliate-checker'
        },
        {
            key: 'live-affiliate-manage',
            label: 'Manage Live Affiliates',
            icon: Edit,
            link: '/live-affiliate-manage',
            route: '/live-affiliate-manage'
        },
        {
            key: 'affiliate-finder-nocost',
            label: 'Affiliate Finder (No Cost)',
            icon: Radar,
            link: '/affiliate-finder-nocost',
            route: '/affiliate-finder-nocost'
        },
        {
            key: 'classify-queue',
            label: 'CSV Classification Queue',
            icon: ListOrdered,
            link: '/classify-queue',
            route: '/classify-queue'
        },
        {
            key: 'referral-queue',
            label: 'Referral Queue',
            icon: Link2,
            link: '/referral-queue',
            route: '/referral-queue'
        },
        {
            key: 'traffic-zero-fill',
            label: 'Fill Zero Traffic',
            icon: Shuffle,
            link: '/traffic-zero-fill',
            route: '/traffic-zero-fill'
        },
        // The whole paid-traffic module lives under one group, the same shape as
        // Competitor Research above.
        {
            key: 'paidTraffic',
            label: 'Paid Traffic',
            icon: DollarSign,
            hasSubmenu: true,
            isOpen: openMenus.paidTraffic || false,
            submenu: [
                { key: 'paid-traffic', label: 'Affiliates', icon: BarChart3, link: '/paid-traffic', route: '/paid-traffic' },
                { key: 'paid-traffic-log', label: 'Paid Traffic Log', icon: FileText, link: '/paid-traffic-log', route: '/paid-traffic-log' },
                { key: 'add-affiliate', label: 'Add Affiliate', icon: Globe, link: '/add-affiliate', route: '/add-affiliate' }
            ]
        },
        ...baseMenuItems.slice(3) // Rest of the menu items, resuming after Competitor Research
    ] :
    baseMenuItems;

// A team member only sees the features their account admin turned on. The
// server refuses the rest anyway; this keeps the menu honest.
const visibleForAccount = (item) => !item.route || !pageBlockReason(account, item.route);
const menuItems = allMenuItems
    .map((item) => (item.hasSubmenu ? { ...item, submenu: item.submenu.filter(visibleForAccount) } : item))
    .filter((item) => (item.hasSubmenu ? item.submenu.length > 0 : visibleForAccount(item)));

// Function to handle menu item clicks
const handleMenuClick = (item) => {
    if (item.hasSubmenu) {
        toggleMenu(item.key);
    } else if (item.route) {
        navigate(item.route);
    } else if (item.link && item.link !== '#') {
        if (item.link.startsWith('mailto:')) {
            window.location.href = item.link;
        } else {
            navigate(item.link);
        }
    }
};

// Function to check if menu item is active
const isMenuItemActive = (item) => {
    if (item.route) {
        return location.pathname === item.route;
    }
    return false;
};

return ( <
    div className = { `fixed left-0 top-0 h-full bg-white/95 dark:bg-slate-900/95 backdrop-blur-sm border-r border-gray-200/60 dark:border-slate-700/60 z-40 transition-all duration-300 ${
      sidebarCollapsed ? '-translate-x-full md:translate-x-0 w-64 md:w-16' : 'translate-x-0 w-64'
    } overflow-hidden` } >

    { /* Sidebar Header - Ultra Clean */ } <
    div className = "h-14 flex items-center justify-center px-4 border-b border-gray-200/40 dark:border-slate-700/40" > {
        sidebarCollapsed ? ( <
            div className = "w-7 h-7 bg-blue-600 rounded-lg flex items-center justify-center shadow-sm" >
            <
            span className = "text-white font-semibold text-xs" > M < /span> < /
            div >
        ) : ( <
            div className = "flex items-center justify-center w-full" >
            <
            div className = "" >
            <
            img src = {isDark ? "/MARS-White.png" : "/MARS-Color.png"}
            alt = "MARS Logo"
            style = {{ width: '70%', display: 'block', margin: '0 auto' }} / >
            <
            /div> < /
            div >
        )
    } <
    /div>

    { /* Impersonation banner - shown while an admin is signed in as another user */ }
    {isImpersonating && (
        <div className={`m-3 rounded-xl border border-amber-300 dark:border-amber-700/60 bg-amber-50 dark:bg-amber-900/20 ${sidebarCollapsed ? 'p-2' : 'p-3'}`}>
            {!sidebarCollapsed && (
                <div className="mb-2">
                    <div className="flex items-center gap-1.5 text-xs font-semibold text-amber-800 dark:text-amber-300">
                        <Eye className="w-3.5 h-3.5 flex-shrink-0" />
                        <span>Viewing as user</span>
                    </div>
                    <div className="mt-0.5 text-[11px] text-amber-700 dark:text-amber-400 truncate" title={businessEmail || ''}>
                        {businessEmail || 'this user'}
                    </div>
                </div>
            )}
            <button
                onClick={handleReturnToAdmin}
                disabled={stoppingImpersonation}
                title={`Return to admin${impersonatorEmail ? ` (${impersonatorEmail})` : ''}`}
                className={`w-full flex items-center justify-center gap-1.5 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-xs font-medium transition-colors disabled:opacity-60 ${sidebarCollapsed ? 'p-2' : 'px-3 py-2'}`}
            >
                <UserCog className="w-4 h-4 flex-shrink-0" />
                {!sidebarCollapsed && <span>{stoppingImpersonation ? 'Returning…' : 'Return to admin'}</span>}
            </button>
        </div>
    )}

    { /* Navigation - Floating Style */ } <
    div className = "p-3 h-full pb-32 overflow-y-auto scrollbar-thin" >
    <
    nav className = "space-y-0.5" > {
        menuItems.map((item) => {
                const isActive = isMenuItemActive(item);

                return ( <
                    div key = { item.key } > { /* Main Menu Item - Sleek Design */ } <
                    div className = { `relative flex items-center justify-between px-3 py-2.5 rounded-xl cursor-pointer transition-all duration-200 group ${
                    isActive
                      ? 'bg-blue-600 text-white shadow-sm'
                      : 'text-gray-600 dark:text-slate-400 hover:bg-gray-50 dark:hover:bg-slate-800 hover:text-gray-900 dark:hover:text-slate-200'
                  }` }
                    onClick = {
                        () => handleMenuClick(item)
                    } >
                    <
                    div className = "flex items-center gap-3 flex-1 min-w-0" >
                    <
                    item.icon className = { `w-4 h-4 flex-shrink-0 ${sidebarCollapsed ? 'mx-auto' : ''} ${
                      isActive ? 'text-white' : 'text-gray-500 dark:text-slate-500 group-hover:text-gray-700 dark:group-hover:text-slate-300'
                    }` }
                    /> {!sidebarCollapsed && ( <
                    span className = "font-medium text-sm truncate" > { item.label } < /span>
                )
            } <
            /div> {!sidebarCollapsed && item.hasSubmenu && ( <
            ChevronDown className = { `w-3.5 h-3.5 flex-shrink-0 transition-transform duration-200 ${
                      item.isOpen ? 'rotate-180' : ''
                    } ${isActive ? 'text-white/80' : 'text-gray-400'}` }
            />
        )
    }

    { /* Active Indicator */ } {
        isActive && ( <
            div className = "absolute right-1 top-1/2 transform -translate-y-1/2 w-1 h-4 bg-white/30 rounded-full" > < /div>
        )
    } <
    /div>

    { /* Submenu - Clean Expansion */ } {
        !sidebarCollapsed && item.hasSubmenu && ( <
            div className = { `overflow-hidden transition-all duration-300 ${
                    item.isOpen ? 'max-h-96 opacity-100 mt-1' : 'max-h-0 opacity-0'
                  }` } >
            <
            div className = "pl-6 pr-3 space-y-0.5" > {
                item.submenu.map((subItem) => ( <
                    Link key = { subItem.key }
                    to = { subItem.link }
                    className = "flex items-center gap-2.5 px-3 py-2 rounded-lg cursor-pointer text-gray-500 dark:text-slate-400 hover:bg-gray-50 dark:hover:bg-slate-800 hover:text-gray-700 dark:hover:text-slate-200 transition-all duration-200 group" >
                    <
                    subItem.icon className = "w-3.5 h-3.5 text-gray-400 dark:text-slate-500 group-hover:text-gray-600 dark:group-hover:text-slate-300 flex-shrink-0" / >
                    <
                    span className = "text-sm truncate" > { subItem.label } < /span> < /
                    Link >
                ))
            } <
            /div> < /
            div >
        )
    } <
    /div>
);
})
} <
/nav> < /
div >

    { /* Footer - Minimal & Clean */ } <
    div className = {`absolute bottom-0 left-0 right-0 p-3 bg-white/80 dark:bg-slate-900/80 backdrop-blur-sm border-t border-gray-200/40 dark:border-slate-700/40`} >

    {!sidebarCollapsed && (
        <div className = "text-center" >
        <div className = "text-xs text-gray-400 dark:text-slate-500" > ©2026 MARS </div>
        </div>
    )}
    </div>
<
/div>
);
};

export default Sidebar;